import type { ProjectSchemaJSON } from '@takeshape/schema';
import type { ProjectSchemaCompat } from 'app/compat/types';
import { type ActionType, createAction } from 'typesafe-actions';
import { NAME } from './constants.js';
import type { createBranchRoutine } from './sagas/create-branch.js';
import type { deleteBranchRoutine } from './sagas/delete-branch.js';
import type { ensureScratchBranchRoutine } from './sagas/ensure-scratch-branch.js';
import type { fetchSchemaRoutine } from './sagas/fetch-schema.js';
import type { fetchServiceLayersRoutine } from './sagas/fetch-service-layers.js';
import type { getBranchListRoutine } from './sagas/get-branch-list.js';
import type { mergeBranchRoutine } from './sagas/merge-branch.js';
import type { postSchemaRoutine } from './sagas/post-schema.js';
import type { updateSchema } from './sagas/update-schema.js';

export type SetCurrentBranchPayload = {
  branch: string;
  message?: string;
  snackbarMessage?: boolean;
};

export type SetWorkingCopyPayload = {
  data: ProjectSchemaCompat | null;
  dataOrigin: string | null;
};

export const resetBranchState = createAction(`${NAME}/RESET_BRANCHES`)();
export const setCurrentBranch = createAction(`${NAME}/SET_CURRENT_BRANCH`)<SetCurrentBranchPayload>();
export const setUseScratchForPreview = createAction(`${NAME}/SET_USE_SCRATCH_FOR_PREVIEW`)<boolean>();
export const setUserSchema = createAction(`${NAME}/SET_USER_SCHEMA`)<{
  branch?: string;
  schema: ProjectSchemaCompat;
}>();
export const setProjectSchema = createAction(`${NAME}/SET_PROJECT_SCHEMA`)<{
  branch?: string;
  schema: ProjectSchemaCompat;
}>();
export const clearScratchState = createAction(`${NAME}/CLEAR_SCRATCH_STATE`)();

export enum BranchMergeOperation {
  Update = 'Update',
  Promote = 'Promote'
}

export type OpenBranchMergePayload = {
  branchName: string;
  operation: BranchMergeOperation;
};

export const openBranchMerge = createAction(`${NAME}/OPEN_BRANCH_MERGE`)<OpenBranchMergePayload>();
export const closeBranchMerge = createAction(`${NAME}/CLOSE_BRANCH_PROMOTION`)();
export const setBranchMergeError = createAction(`${NAME}/SET_BRANCH_PROMOTION_ERROR`)<string>();
export const setBranchMergeCanMerge = createAction(`${NAME}/SET_BRANCH_MERGE_CAN_MERGE`)<ProjectSchemaJSON>();
export const setBranchMergeInProgress = createAction(`${NAME}/SET_BRANCH_MERGE_IN_PROGRESS`)<boolean>();
export const setCurrentBranchNeedsUpdate = createAction(`${NAME}/SET_CURRENT_BRANCH_NEEDS_UPDATE`)<boolean | null>();
export const setCurrentBranchCanPromote = createAction(`${NAME}/SET_CURRENT_BRANCH_CAN_PROMOTE`)<boolean | null>();

export type BranchesAction =
  | ActionType<typeof setCurrentBranch>
  | ActionType<typeof resetBranchState>
  | ActionType<typeof setUseScratchForPreview>
  | ActionType<typeof setUserSchema>
  | ActionType<typeof setProjectSchema>
  | ActionType<typeof updateSchema>
  | ActionType<typeof fetchSchemaRoutine>
  | ActionType<typeof fetchServiceLayersRoutine>
  | ActionType<typeof postSchemaRoutine>
  | ActionType<typeof createBranchRoutine>
  | ActionType<typeof deleteBranchRoutine>
  | ActionType<typeof getBranchListRoutine>
  | ActionType<typeof mergeBranchRoutine>
  | ActionType<typeof openBranchMerge>
  | ActionType<typeof closeBranchMerge>
  | ActionType<typeof setBranchMergeError>
  | ActionType<typeof setBranchMergeCanMerge>
  | ActionType<typeof setBranchMergeInProgress>
  | ActionType<typeof setCurrentBranchNeedsUpdate>
  | ActionType<typeof setCurrentBranchCanPromote>
  | ActionType<typeof clearScratchState>
  | ActionType<typeof ensureScratchBranchRoutine>;
